import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import pdfWorker from 'react-pdf/node_modules/pdfjs-dist/build/pdf.worker.min.mjs?url';
import Icon from '../../components/AppIcon';
import {
  createFolder,
  uploadFile,
  listFolders,
  listFiles,
  getFileSignedUrl,
  deleteFile,
  deleteFolder,
  renameFile,
} from '../../services/projectDocumentsService';
import { supabase } from '../../lib/supabase';
import { projectService } from '../../services/projectService';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import useTranslation from '../../hooks/useTranslation';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

// ─── Helpers ──────────────────────────────────────────────────
const formatBytes = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
};

const formatDate = (iso, language) => {
  if (!iso) return '—';
  return new Date(iso)?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fileIcon = (fileType, fileName) => {
  const name = (fileName || '')?.toLowerCase();
  if (/\.pdf$/i?.test(name)) return 'FileText';
  if (/\.(jpg|jpeg|png|gif|webp)$/i?.test(name)) return 'Image';
  if (/\.(xls|xlsx|csv)$/i?.test(name)) return 'Table';
  if (/\.(doc|docx)$/i?.test(name)) return 'FileType';
  return 'File';
};

// ─── Build nested tree from flat list ─────────────────────────
const buildTree = (folders) => {
  const map = {};
  folders?.forEach((f) => { map[f.id] = { ...f, children: [] }; });
  const roots = [];
  folders?.forEach((f) => {
    if (f?.parent_id && map?.[f?.parent_id]) {
      map?.[f?.parent_id]?.children?.push(map?.[f?.id]);
    } else {
      roots?.push(map?.[f?.id]);
    }
  });
  return roots;
};

// ─── PDF Viewer (reused from invoice-inbox pattern) ───────────
const FilePreviewPanel = ({ file, onClose }) => {
  const { t } = useTranslation();
  const [signedUrl, setSignedUrl] = useState(null);
  const [loadState, setLoadState] = useState('loading');
  const [numPages, setNumPages] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!file) return;
    setLoadState('loading');
    setSignedUrl(null);
    setNumPages(null);
    setZoom(1);
    getFileSignedUrl(file?.file_path, 3600)?.then(({ signedUrl: url }) => {
      if (url) setSignedUrl(url);
      else setLoadState('error');
    });
  }, [file?.id]);

  if (!file) return null;

  const lowerName = (file?.file_name || '')?.toLowerCase();
  const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i?.test(lowerName);
  const isPdf = /\.pdf$/i?.test(lowerName);

  return (
    <div className="flex flex-col h-full">
      {/* Preview header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <Icon name={fileIcon(file?.file_type, file?.file_name)} size={16} className="text-gray-400 shrink-0" />
          <span className="text-sm font-medium text-gray-800 truncate">{file?.file_name}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {signedUrl && (
            <a
              href={signedUrl}
              download={file?.file_name}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Icon name="Download" size={13} />
              {t('documents.preview.download')}
            </a>
          )}
          {/* Zoom controls */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-1 py-0.5">
            <button
              onClick={() => setZoom(z => Math.max(0.5, parseFloat((z - 0.25)?.toFixed(2))))}
              className="w-6 h-6 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors text-base font-medium"
              title={t('documents.preview.zoomOut')}
            >
              −
            </button>
            <span className="text-xs text-gray-500 w-10 text-center select-none">{Math.round(zoom * 100)}%</span>
            <button
              onClick={() => setZoom(z => Math.min(3, parseFloat((z + 0.25)?.toFixed(2))))}
              className="w-6 h-6 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors text-base font-medium"
              title={t('documents.preview.zoomIn')}
            >
              +
            </button>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      </div>
      {/* Preview body */}
      <div className="flex-1 overflow-y-auto bg-gray-50 flex flex-col items-center py-4 px-2 gap-3 relative">
        {(loadState === 'loading' || !signedUrl) && loadState !== 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-50 z-10">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin" />
            <p className="text-sm text-gray-500">{t('documents.preview.loadingFile')}</p>
          </div>
        )}
        {loadState === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-50 z-10">
            <Icon name="AlertTriangle" size={28} className="text-amber-400" />
            <p className="text-sm text-gray-500">{t('documents.preview.unableToLoad')}</p>
            {signedUrl && (
              <a href={signedUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg">
                <Icon name="ExternalLink" size={13} />
                {t('documents.preview.openInNewTab')}
              </a>
            )}
          </div>
        )}
        {signedUrl && loadState !== 'error' && (
          isImage ? (
            <img
              src={signedUrl}
              alt={file?.file_name}
              className="shadow-md rounded transition-transform duration-150"
              style={{ maxWidth: '100%', transform: `scale(${zoom})`, transformOrigin: 'top center' }}
              onLoad={() => setLoadState('loaded')}
              onError={() => setLoadState('error')}
            />
          ) : isPdf ? (
            <Document
              file={signedUrl}
              onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoadState('loaded'); }}
              onLoadError={() => setLoadState('error')}
              loading={null}
            >
              {numPages
                ? Array.from(new Array(numPages), (_, i) => (
                    <Page
                      key={`page_${i + 1}`}
                      pageNumber={i + 1}
                      width={Math.min(window.innerWidth * 0.45, 600) * zoom}
                      className="shadow-md rounded mb-2"
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  ))
                : null}
            </Document>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 mt-16">
              <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center">
                <Icon name={fileIcon(file?.file_type, file?.file_name)} size={32} className="text-gray-400" />
              </div>
              <p className="text-sm text-gray-500">{t('documents.preview.notAvailable')}</p>
              <a href={signedUrl} download={file?.file_name}
                className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg">
                <Icon name="Download" size={13} />
                {t('documents.preview.downloadFile')}
              </a>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// ─── Folder Tree Node ─────────────────────────────────────────
const FolderNode = ({ node, selectedId, onSelect, onRename, onDelete, depth = 0, fileCounts = {} }) => {
  const { t, language } = useTranslation();
  const [open, setOpen] = useState(depth === 0);
  const isSelected = selectedId === node?.id;
  const hasChildren = node?.children?.length > 0;

  const lastUpdated = node?.updated_at || node?.created_at;
  const formattedDate = lastUpdated
    ? new Date(lastUpdated)?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const subfolderCount = node?.children?.length || 0;
  const docCount = fileCounts?.[node?.id] ?? 0;
  const totalCount = subfolderCount + docCount;

  return (
    <div>
      <div
        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg cursor-pointer group transition-colors ${
          isSelected ? 'bg-orange-50 text-orange-600' : 'text-gray-700 hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        onClick={() => { onSelect(node); if (hasChildren) setOpen((o) => !o); }}
      >
        {hasChildren ? (
          <Icon name={open ? 'ChevronDown' : 'ChevronRight'} size={14} className="shrink-0 text-gray-400" />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <Icon name={open && hasChildren ? 'FolderOpen' : 'Folder'} size={15} className={isSelected ? 'text-orange-500' : 'text-gray-400'} />
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span className="text-sm truncate font-medium">{node?.name}</span>
          {totalCount > 0 && (
            <span className="text-[10px] text-gray-400 font-normal shrink-0">{totalCount}</span>
          )}
        </div>
        {/* Actions */}
        <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
          {formattedDate && (
            <span className="text-[10px] text-gray-400 leading-tight mr-1">{formattedDate}</span>
          )}
          <button
            onClick={(e) => { e?.stopPropagation(); onRename(node); }}
            className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
            title={t('documents.renameAction')}
          >
            <Icon name="Pencil" size={12} />
          </button>
          <button
            onClick={(e) => { e?.stopPropagation(); onDelete(node); }}
            className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-500"
            title={t('documents.deleteAction')}
          >
            <Icon name="Trash2" size={12} />
          </button>
        </div>
      </div>
      {open && hasChildren && (
        <div>
          {node?.children?.map((child) => (
            <FolderNode
              key={child?.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              onRename={onRename}
              onDelete={onDelete}
              depth={depth + 1}
              fileCounts={fileCounts}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main DocumentsPage ───────────────────────────────────────
const DocumentsPage = () => {
  const { t, language } = useTranslation();
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  // Project info for header
  const [project, setProject] = useState(null);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderFileCounts, setFolderFileCounts] = useState({});

  // File state
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  // Loading
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Modals
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [createFolderParent, setCreateFolderParent] = useState(null);
  const [folderModalStep, setFolderModalStep] = useState('choose'); // 'choose' | 'name'

  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'folder'|'file', item }
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Inline file rename state
  const [renamingFileId, setRenamingFileId] = useState(null);
  const [renamingFileName, setRenamingFileName] = useState('');
  const renameFileInputRef = useRef(null);

  const fileInputRef = useRef(null);

  // ── Load file counts for all folders ─────────────────────
  const loadFolderFileCounts = useCallback(async (folderList) => {
    if (!folderList?.length) { setFolderFileCounts({}); return; }
    const folderIds = folderList?.map((f) => f?.id);
    const { data } = await supabase
      ?.from('project_files')
      ?.select('folder_id')
      ?.in('folder_id', folderIds);
    if (data) {
      const counts = {};
      data?.forEach((row) => {
        counts[row?.folder_id] = (counts?.[row?.folder_id] || 0) + 1;
      });
      setFolderFileCounts(counts);
    }
  }, []);

  // ── Load folders ──────────────────────────────────────────
  const loadFolders = useCallback(async () => {
    if (!projectId) return;
    setLoadingFolders(true);
    const { data } = await listFolders(projectId);
    const flat = data || [];
    setFolders(flat);
    setFolderTree(buildTree(flat));
    setLoadingFolders(false);
    loadFolderFileCounts(flat);
  }, [projectId, loadFolderFileCounts]);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  // ── Load project info ─────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    projectService?.getById(projectId)?.then((data) => {
      if (data) setProject(data);
    });
  }, [projectId]);

  // ── Load files when folder changes ───────────────────────
  const loadFiles = useCallback(async (folderId) => {
    if (!folderId) { setFiles([]); return; }
    setLoadingFiles(true);
    setSelectedFile(null);
    const { data } = await listFiles(folderId);
    setFiles(data || []);
    setLoadingFiles(false);
  }, []);

  useEffect(() => {
    if (selectedFolder) loadFiles(selectedFolder?.id);
    else setFiles([]);
  }, [selectedFolder, loadFiles]);

  // ── Breadcrumb path ───────────────────────────────────────
  const getBreadcrumb = () => {
    if (!selectedFolder) return [];
    const path = [];
    const map = {};
    folders?.forEach((f) => { map[f.id] = f; });
    let cur = selectedFolder;
    while (cur) {
      path?.unshift(cur);
      cur = cur?.parent_id ? map?.[cur?.parent_id] : null;
    }
    return path;
  };

  // ── Create folder ─────────────────────────────────────────
  const handleCreateFolder = async () => {
    if (!newFolderName?.trim()) return;
    const { error } = await createFolder(projectId, newFolderName?.trim(), createFolderParent?.id || null);
    if (!error) {
      setNewFolderName('');
      setShowCreateFolder(false);
      setCreateFolderParent(null);
      setFolderModalStep('choose');
      await loadFolders();
    }
  };

  // ── Rename folder ─────────────────────────────────────────
  const handleRenameFolder = async () => {
    if (!renameName?.trim() || !renameTarget) return;
    const { error } = await supabase?.from('project_folders')?.update({ name: renameName?.trim() })?.eq('id', renameTarget?.id);
    if (!error) {
      setRenameTarget(null);
      setRenameName('');
      await loadFolders();
    }
  };

  // ── Delete folder ─────────────────────────────────────────
  const handleDeleteFolder = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    const { error } = await deleteFolder(deleteTarget?.item?.id);
    if (!error) {
      if (selectedFolder?.id === deleteTarget?.item?.id) setSelectedFolder(null);
      await loadFolders();
    }
    setDeleteLoading(false);
    setDeleteTarget(null);
  };

  // ── Delete file ───────────────────────────────────────────
  const handleDeleteFile = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    const { error } = await deleteFile(deleteTarget?.item?.id, deleteTarget?.item?.file_path);
    if (!error) {
      if (selectedFile?.id === deleteTarget?.item?.id) setSelectedFile(null);
      await loadFiles(selectedFolder?.id);
      await loadFolderFileCounts(folders);
    }
    setDeleteLoading(false);
    setDeleteTarget(null);
  };

  // ── Inline file rename ────────────────────────────────────
  const startFileRename = (e, file) => {
    e?.stopPropagation();
    setRenamingFileId(file?.id);
    setRenamingFileName(file?.file_name);
    setTimeout(() => renameFileInputRef?.current?.focus(), 50);
  };

  const commitFileRename = async () => {
    if (!renamingFileName?.trim() || !renamingFileId) { cancelFileRename(); return; }
    const { error } = await renameFile(renamingFileId, renamingFileName?.trim());
    if (!error) {
      await loadFiles(selectedFolder?.id);
    }
    setRenamingFileId(null);
    setRenamingFileName('');
  };

  const cancelFileRename = () => {
    setRenamingFileId(null);
    setRenamingFileName('');
  };

  const handleRenameKeyDown = (e) => {
    if (e?.key === 'Enter') { e?.preventDefault(); commitFileRename(); }
    if (e?.key === 'Escape') { cancelFileRename(); }
  };

  // ── Upload file ───────────────────────────────────────────
  const handleUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !selectedFolder) return;
    setUploading(true);
    await uploadFile(projectId, selectedFolder?.id, file);
    await loadFiles(selectedFolder?.id);
    await loadFolderFileCounts(folders);
    setUploading(false);
    e.target.value = '';
  };

  // ── Download file ─────────────────────────────────────────
  const handleDownload = async (file) => {
    const { signedUrl } = await getFileSignedUrl(file?.file_path, 60);
    if (signedUrl) {
      const a = document.createElement('a');
      a.href = signedUrl;
      a.download = file?.file_name;
      a?.click();
    }
  };

  const breadcrumb = getBreadcrumb();

  return (
    <div className="flex flex-col h-full gap-0">
      {/* Page header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="FolderOpen" size={20} className="text-primary" />
          <h1 className="text-lg font-semibold text-[#2C3E50]">{t('documents.title')}</h1>
        </div>
        <div className="flex items-center gap-1 mb-1">
          <span
            onClick={() => navigate('/project-detail')}
            className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
          >
            {project?.projectNumber || '—'}
          </span>
          <span className="text-sm text-primary font-normal">-</span>
          <span
            onClick={() => navigate('/project-detail')}
            className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
          >
            {project?.name || ''}
          </span>
        </div>
        <p className="text-xs text-gray-500">{t('documents.subtitle')}</p>
      </div>
      <div className="space-y-4 flex-1 min-h-0 flex flex-col">
        {/* TOP TOOLBAR */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder={t('documents.searchPlaceholder')}
              className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
          </div>
        </div>

        {/* MAIN GRID */}
        <div className="grid grid-cols-12 gap-6 flex-1 min-h-0" style={{ height: 'calc(100vh - 240px)' }}>

          {/* LEFT: Folder Tree (col-span-4) */}
          <div className="col-span-4 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Folder panel header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-700">{t('documents.foldersPanel')}</span>
              <button
                onClick={() => { setCreateFolderParent(selectedFolder || null); setFolderModalStep('choose'); setShowCreateFolder(true); }}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
              >
                <Icon name="FolderPlus" size={13} />
                {t('documents.newFolderShort')}
              </button>
            </div>

            {/* Tree */}
            <div className="flex-1 overflow-y-auto p-2">
              {loadingFolders ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin" />
                </div>
              ) : folderTree?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-center px-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                    <Icon name="FolderOpen" size={20} className="text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500">{t('documents.noFoldersYet')}</p>
                  <button
                    onClick={() => { setCreateFolderParent(null); setFolderModalStep('choose'); setShowCreateFolder(true); }}
                    className="text-xs text-orange-500 hover:underline"
                  >
                    {t('documents.createFirstFolder')}
                  </button>
                </div>
              ) : (
                folderTree?.map((node) => (
                  <FolderNode
                    key={node?.id}
                    node={node}
                    selectedId={selectedFolder?.id}
                    onSelect={setSelectedFolder}
                    onRename={(f) => { setRenameTarget(f); setRenameName(f?.name); }}
                    onDelete={(f) => setDeleteTarget({ type: 'folder', item: f })}
                    fileCounts={folderFileCounts}
                  />
                ))
              )}
            </div>
          </div>

          {/* RIGHT: File List (col-span-8, full width now since preview is modal) */}
          <div className="col-span-8 flex flex-col gap-4 min-w-0 min-h-0">
            <div className="flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex-1 min-h-0">
              {/* File list header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-700">
                  {selectedFolder ? selectedFolder?.name : t('documents.selectAFolder')}
                </span>
                {selectedFolder && (
                  <button
                    onClick={() => fileInputRef?.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark disabled:opacity-60 rounded-lg transition-colors"
                  >
                    {uploading ? (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Icon name="Upload" size={13} />
                    )}
                    {uploading ? t('documents.uploading') : t('documents.uploadFile')}
                  </button>
                )}
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
              </div>

              {/* File rows */}
              <div className="flex-1 overflow-y-auto">
                {!selectedFolder ? (
                  <div className="flex flex-col items-center justify-center h-full gap-2 text-center px-6 py-10">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                      <Icon name="Folder" size={24} className="text-gray-300" />
                    </div>
                    <p className="text-sm text-gray-400">{t('documents.selectFolderToViewFiles')}</p>
                  </div>
                ) : loadingFiles ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-5 h-5 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin" />
                  </div>
                ) : files?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-2 text-center px-6 py-10">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                      <Icon name="FileX" size={24} className="text-gray-300" />
                    </div>
                    <p className="text-sm text-gray-500 font-medium">{t('documents.noFilesYet')}</p>
                    <p className="text-xs text-gray-400">{t('documents.uploadToGetStarted')}</p>
                    <button
                      onClick={() => fileInputRef?.current?.click()}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg mt-1"
                    >
                      <Icon name="Upload" size={13} />
                      {t('documents.uploadFile')}
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {files?.map((file) => {
                      const isRenaming = renamingFileId === file?.id;
                      return (
                        <div
                          key={file?.id}
                          onClick={() => !isRenaming && setSelectedFile(file)}
                          className="flex items-center gap-3 px-4 py-3 cursor-pointer group transition-colors hover:bg-gray-50"
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gray-100">
                            <Icon name={fileIcon(file?.file_type, file?.file_name)} size={16} className="text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            {isRenaming ? (
                              <div className="flex items-center gap-1" onClick={(e) => e?.stopPropagation()}>
                                <input
                                  ref={renameFileInputRef}
                                  value={renamingFileName}
                                  onChange={(e) => setRenamingFileName(e?.target?.value)}
                                  onKeyDown={handleRenameKeyDown}
                                  onBlur={commitFileRename}
                                  className="flex-1 text-sm font-medium text-gray-800 border border-orange-400 rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary min-w-0"
                                />
                                <button
                                  onMouseDown={(e) => { e?.preventDefault(); commitFileRename(); }}
                                  className="p-1 rounded hover:bg-orange-100 text-orange-500 transition-colors"
                                  title={t('documents.confirmAction')}
                                >
                                  <Icon name="Check" size={13} />
                                </button>
                                <button
                                  onMouseDown={(e) => { e?.preventDefault(); cancelFileRename(); }}
                                  className="p-1 rounded hover:bg-gray-200 text-gray-400 transition-colors"
                                  title={t('documents.cancelAction')}
                                >
                                  <Icon name="X" size={13} />
                                </button>
                              </div>
                            ) : (
                              <p className="text-sm font-medium truncate text-gray-800">{file?.file_name}</p>
                            )}
                            <p className="text-xs text-gray-400">{formatBytes(file?.file_size)} · {formatDate(file?.created_at, language)}</p>
                          </div>
                          {/* Row actions */}
                          {!isRenaming && (
                            <div className="hidden group-hover:flex items-center gap-1 shrink-0">
                              <button
                                onClick={(e) => startFileRename(e, file)}
                                className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                                title={t('documents.renameAction')}
                              >
                                <Icon name="Pencil" size={14} />
                              </button>
                              <button
                                onClick={(e) => { e?.stopPropagation(); handleDownload(file); }}
                                className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                                title={t('documents.downloadAction')}
                              >
                                <Icon name="Download" size={14} />
                              </button>
                              <button
                                onClick={(e) => { e?.stopPropagation(); setDeleteTarget({ type: 'file', item: file }); }}
                                className="p-1.5 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                                title={t('documents.deleteAction')}
                              >
                                <Icon name="Trash2" size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ── File Preview Modal ── */}
      {selectedFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedFile(null)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            style={{ width: '90vw', maxWidth: '1100px', height: '90vh' }}
          >
            <FilePreviewPanel file={selectedFile} onClose={() => setSelectedFile(null)} />
          </div>
        </div>
      )}
      {/* ── Create Folder Modal ── */}
      {showCreateFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => { setShowCreateFolder(false); setNewFolderName(''); setCreateFolderParent(null); setFolderModalStep('choose'); }}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">{t('documents.createFolder.title')}</h2>
                <p className="text-xs text-gray-500 mt-0.5">{t('documents.createFolder.subtitle')}</p>
              </div>
              <button
                onClick={() => { setShowCreateFolder(false); setNewFolderName(''); setCreateFolderParent(null); setFolderModalStep('choose'); }}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 flex flex-col gap-5">
              {/* Folder Name Input */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('documents.createFolder.nameLabel')}</label>
                <input
                  autoFocus
                  type="text"
                  value={newFolderName}
                  onChange={(e) => {
                    const val = e?.target?.value;
                    setNewFolderName(val ? val?.charAt(0)?.toUpperCase() + val?.slice(1) : val);
                  }}
                  onKeyDown={(e) => e?.key === 'Enter' && newFolderName?.trim() && handleCreateFolder()}
                  placeholder={t('documents.createFolder.namePlaceholder')}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white transition-colors"
                />
              </div>

              {/* Type Toggle */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('documents.createFolder.typeLabel')}</label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setCreateFolderParent(null)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                      !createFolderParent
                        ? 'bg-primary text-white' :'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon name="Folder" size={13} />
                    {t('documents.createFolder.rootOption')}
                  </button>
                  <button
                    type="button"
                    disabled={!selectedFolder}
                    onClick={() => { if (selectedFolder) setCreateFolderParent(selectedFolder); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-l border-gray-200 ${
                      createFolderParent
                        ? 'bg-primary text-white'
                        : selectedFolder
                          ? 'bg-white text-gray-600 hover:bg-gray-50' :'bg-gray-50 text-gray-300 cursor-not-allowed'
                    }`}
                  >
                    <Icon name="FolderTree" size={13} />
                    {t('documents.createFolder.subfolderOption')}
                  </button>
                </div>
                {createFolderParent && (
                  <p className="text-xs text-gray-400 mt-1.5">
                    {t('documents.createFolder.insideLabel')} <span className="font-medium text-gray-600">{createFolderParent?.name}</span>
                  </p>
                )}
                {!selectedFolder && (
                  <p className="text-xs text-gray-400 mt-1.5">{t('documents.createFolder.selectFolderFirst')}</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 flex gap-3 justify-end">
              <button
                onClick={() => { setShowCreateFolder(false); setNewFolderName(''); setCreateFolderParent(null); setFolderModalStep('choose'); }}
                className="px-4 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName?.trim()}
                className="px-4 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-1.5"
              >
                <Icon name="FolderPlus" size={13} />
                {t('documents.createFolder.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Rename Folder Modal ── */}
      {renameTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-3">{t('documents.renameFolder.title')}</h3>
            <input
              autoFocus
              type="text"
              value={renameName}
              onChange={(e) => setRenameName(e?.target?.value)}
              onKeyDown={(e) => e?.key === 'Enter' && handleRenameFolder()}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => { setRenameTarget(null); setRenameName(''); }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleRenameFolder}
                disabled={!renameName?.trim()}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark disabled:opacity-50 rounded-xl transition-colors"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Delete Confirmation Modal ── */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        confirmVariant="danger"
        title={deleteTarget?.type === 'folder' ? t('documents.confirmDelete.folderTitle') : t('documents.confirmDelete.fileTitle')}
        message={
          deleteTarget?.type === 'folder' ? t('documents.confirmDelete.folderMessage') : t('documents.confirmDelete.fileMessage')
        }
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        loading={deleteLoading}
        onConfirm={deleteTarget?.type === 'folder' ? handleDeleteFolder : handleDeleteFile}
        onCancel={() => setDeleteTarget(null)}
      >
        <p className="text-sm text-gray-500 truncate">"{deleteTarget?.item?.name || deleteTarget?.item?.file_name}"</p>
      </ConfirmDialog>
    </div>
  );
};

export default DocumentsPage;
